import { useEffect, useMemo, useState } from 'react'
import useTasks from '../models/useTasks'
import useNode, { isNodeOffline } from '../models/useNode'
import useApp from '../models/useApp'
import useOrganizations from '../models/useOrganizations'
import { hasOpenTask, openComputeByNode, type NodeCompute } from '../utilities/computeNode'

/**
 * Which nodes have compute attached, keyed by node id (app#329).
 *
 * The rule is in `utilities/computeNode.ts`: a node has compute while it has an OPEN task. This
 * hook only READS `useTasks` and adds a clock, so the "stops in" text moves while the page is open.
 * It does not fetch. `useComputeNodesLoader` does, and it is mounted ONCE, by the graph.
 *
 * ⚠ Read and load are two hooks on purpose. Every surface that shows compute (the graph's and
 * the list's noBGP cloud mark, the node page header's badge) calls this one; if each of them also loaded, one screen would
 * send the same `tasks` read two or three times, and poll it two or three times.
 */

// The label prints whole minutes, so the clock does not need to move faster than this.
const TICK_MS = 30_000

export default function useComputeNodes(): Map<string, NodeCompute> {
  const tasks = useTasks(s => s.tasks)
  const classes = useTasks(s => s.classes)
  const [tick, setTick] = useState(0)

  const open = hasOpenTask(tasks)

  // ⚠ The clock runs only while a task is open. With no open task there is no badge and no text
  // to update, and a tick would re-render the graph for nothing.
  useEffect(() => {
    if (!open) return
    const id = setInterval(() => setTick(t => t + 1), TICK_MS)
    return () => clearInterval(id)
  }, [open])

  // ⚠⚠ THE TIME IS READ HERE, NOT KEPT IN STATE. The clock above is stopped while no task is open,
  // so a time kept from the last tick can be hours old when the first badge appears, and the label
  // then says "stops in …" for a deadline that has passed. The map changes only when the rows or
  // the tick change, so the graph still gets the same reference between them.
  return useMemo(() => openComputeByNode(tasks, classes, new Date()), [tasks, classes, tick])
}

// Same interval as the Provisioned machines table, and for the same reason: `tasks` is not in the
// realtime publication, and the router's compute-stop sweeper runs every 10 minutes. A badge must
// go away within one sweep of the machine stopping.
const REFRESH_MS = 120_000

// How long the loader waits for the node rows to stop changing before it reads.
const SETTLE_MS = 1_000

/**
 * Loads the `tasks` rows that `useComputeNodes` reads. Mount it ONCE — the graph does.
 *
 * It re-reads:
 * - when the active org changes, because the rows are scoped to one org;
 * - when the number of nodes changes, because a provisioned container that registers is a new
 *   node, and that is the moment its task gets a `node_id` to badge;
 * - when the number of ONLINE nodes changes, because a node provisioned again under the same name
 *   keeps its id, so the node count does not move, but the node goes offline and comes back;
 * - every 2 minutes while a task is open, because nothing pushes a stop.
 *
 * ⚠⚠ THE READ WAITS FOR THE APP TO LOAD AND FOR THE NODES TO SETTLE. `controller.loadState` and an
 * org switch set the org FIRST and load the nodes LATER, so each of them changes these values more
 * than once, and realtime node events come in bursts. Without the `loaded` gate and the
 * `SETTLE_MS` delay, each change is one more `tasks` + `compute_classes` read.
 *
 * ⚠ It calls `useTasks.getState().get({ background: true })`: the same read the Usage screen uses,
 * without `loading` and without writing `error`, so it cannot erase or add a message on that
 * screen. That read re-checks the active org before it writes, so a slow answer for the previous
 * org cannot land here.
 */
export function useComputeNodesLoader(): void {
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  // ⚠ A primitive, not the array: a Zustand v5 selector must return a stable value.
  const nodeCount = useNode(s => s.nodes.length)
  const onlineCount = useNode(s => s.nodes.filter(n => !isNodeOffline(n)).length)
  const loaded = useApp(s => s.loaded)
  const open = useTasks(s => hasOpenTask(s.tasks))

  useEffect(() => {
    if (!loaded || !activeOrgId) return
    const id = setTimeout(() => void useTasks.getState().get({ background: true }), SETTLE_MS)
    return () => clearTimeout(id)
  }, [loaded, activeOrgId, nodeCount, onlineCount])

  useEffect(() => {
    if (!loaded || !activeOrgId || !open) return
    const id = setInterval(() => void useTasks.getState().get({ background: true }), REFRESH_MS)
    return () => clearInterval(id)
  }, [loaded, activeOrgId, open])
}
